import type { Skill } from '../skillsData';

// ===== DeFi Extra — from skills/defi/ (previously missing) =====

export const cryptoskillDefiExtraData: Skill[] = [

  // ── ERC-4626 Vaults ──
  {
    id: '4626-agent',
    name: '4626-agent',
    author: 'ERC-4626 Hub',
    authorHandle: '@erc4626',
    tag: 'Skill',
    category: 'defi',
    description: 'ERC-4626 tokenized vault agent. Deposit/withdraw from any standard vault, compare yield, and automate vault strategy switching.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the 4626-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/4626-agent',
    usageExample: '> deposit to ERC-4626 vault\n> compare vault APY\n> auto-switch strategy',
    detailedDescription: 'ERC-4626 standard vault integration supporting deposit, withdrawal, and yield tracking across any compliant tokenized vault. Automate vault strategy switching based on real-time APY comparisons.',
    featured: false,
    trending: false,
  },
  {
    id: '4626-vault-agent',
    name: '4626-vault-agent',
    author: 'ERC-4626 Hub',
    authorHandle: '@erc4626',
    tag: 'Skill',
    category: 'defi',
    description: 'Advanced ERC-4626 vault management. Multi-vault rebalancing, yield optimization, and risk-adjusted allocation across tokenized vaults.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the 4626-vault-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/4626-vault-agent',
    usageExample: '> rebalance vault allocation\n> optimize yield\n> set risk parameters',
    detailedDescription: 'Advanced ERC-4626 vault management with multi-vault rebalancing, risk-adjusted yield optimization, and automated allocation across tokenized vaults on Ethereum, Base, and Arbitrum.',
  },

  // ── Aave Variants ──
  {
    id: 'aave-heyanon',
    name: 'aave-heyanon',
    author: 'Aave',
    authorHandle: '@aave',
    tag: 'Skill',
    category: 'defi',
    description: 'Aave HeyAnon integration. Anonymous Aave lending/borrowing with privacy-preserving ZK proofs and stealth addresses.',
    downloads: 7400,
    stars: 370,
    version: '1.0.0',
    installCommand: '> install the aave-heyanon skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aave-heyanon',
    usageExample: '> supply anonymously\n> borrow with ZK proof\n> manage stealth position',
    detailedDescription: 'Aave lending and borrowing with HeyAnon privacy layer. Supply and borrow assets anonymously using ZK proofs and stealth address protocols while maintaining Aave v3 compatibility.',
  },
  {
    id: 'aave-lending-agent',
    name: 'aave-lending-agent',
    author: 'Aave',
    authorHandle: '@aave',
    tag: 'Skill',
    category: 'defi',
    description: 'Aave lending automation agent. Auto-compound interest, auto-repay, and health factor maintenance across Aave v3 markets.',
    downloads: 28600,
    stars: 1430,
    version: '2.0.0',
    installCommand: '> install the aave-lending-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aave-lending-agent',
    usageExample: '> auto-compound interest\n> auto-repay at threshold\n> maintain health factor',
    detailedDescription: 'Aave automated lending agent with auto-compounding, health-factor-triggered repayments, and cross-chain position monitoring to prevent liquidation.',
    trending: true,
  },
  {
    id: 'aave-liquidation-monitor',
    name: 'aave-liquidation-monitor',
    author: 'Aave',
    authorHandle: '@aave',
    tag: 'Skill',
    category: 'defi',
    description: 'Aave liquidation monitor. Real-time health factor tracking, liquidation bot integration, and at-risk position alerts across all Aave deployments.',
    downloads: 18400,
    stars: 920,
    version: '1.5.0',
    installCommand: '> install the aave-liquidation-monitor skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aave-liquidation-monitor',
    usageExample: '> monitor health factors\n> alert on risk\n> trigger liquidation bot',
    detailedDescription: 'Real-time Aave liquidation monitoring across all v3 deployments. Track health factors, receive alerts when positions approach liquidation thresholds, and integrate liquidation bots for automated execution.',
  },
  {
    id: 'aave-mcp-server',
    name: 'aave-mcp-server',
    author: 'Aave',
    authorHandle: '@aave',
    tag: 'Skill',
    category: 'defi',
    description: 'Aave MCP server. Model Context Protocol access to Aave v3 markets — supply, borrow, repay, and position monitoring via standardized MCP.',
    downloads: 12600,
    stars: 630,
    version: '1.0.0',
    installCommand: '> install the aave-mcp-server skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aave-mcp-server',
    usageExample: '> query aave via MCP\n> supply collateral\n> monitor positions',
    detailedDescription: 'Official Aave MCP server exposing supply, borrow, repay, withdraw, and position monitoring via standardized Model Context Protocol interfaces for seamless AI agent integration.',
  },

  // ── Abstract / Other chains DeFi ──
  {
    id: 'abstract-xp-agent',
    name: 'abstract-xp-agent',
    author: 'Abstract Chain',
    authorHandle: '@abstractchain',
    tag: 'Skill',
    category: 'defi',
    description: 'Abstract Chain XP farming agent. Accumulate Abstract XP through DeFi activity, track points, and optimize XP-generating strategies.',
    downloads: 8200,
    stars: 410,
    version: '1.0.0',
    installCommand: '> install the abstract-xp-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/abstract-xp-agent',
    usageExample: '> farm abstract XP\n> track points balance\n> optimize XP strategy',
    detailedDescription: 'Abstract Chain XP accumulation agent for optimizing DeFi interactions that generate XP points. Track balances, analyze point-per-dollar efficiency, and automate high-yield XP farming strategies.',
  },

  // ── Aegis Protocol ──
  {
    id: 'aegis-protocol',
    name: 'aegis-protocol',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aegis DeFi security protocol. Real-time on-chain threat detection, smart contract circuit breakers, and automated position protection.',
    downloads: 11400,
    stars: 570,
    version: '1.2.0',
    installCommand: '> install the aegis-protocol skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegis-protocol',
    usageExample: '> enable position protection\n> detect on-chain threats\n> trigger circuit breaker',
    detailedDescription: 'Aegis Protocol DeFi security suite with real-time threat detection, automated circuit breakers for smart contract exploits, and position protection mechanisms across major DeFi protocols.',
  },
  {
    id: 'aegis-protocol-defi-security',
    name: 'aegis-protocol-defi-security',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aegis DeFi security scanner. Audit live DeFi positions, detect rugpull patterns, and analyze protocol risk scores before investing.',
    downloads: 9600,
    stars: 480,
    version: '1.1.0',
    installCommand: '> install the aegis-protocol-defi-security skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegis-protocol-defi-security',
    usageExample: '> scan protocol risk\n> detect rugpull patterns\n> audit live position',
    detailedDescription: 'Aegis DeFi security scanner analyzing protocol risk scores, admin key risks, rugpull indicators, and live position exposure. Pre-investment security reports for DeFi participants.',
  },
  {
    id: 'aegis-protocol-security',
    name: 'aegis-protocol-security',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aegis protocol security framework. Continuous monitoring of DeFi protocol health, exploit detection, and emergency withdrawal automation.',
    downloads: 7800,
    stars: 390,
    version: '1.0.0',
    installCommand: '> install the aegis-protocol-security skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegis-protocol-security',
    usageExample: '> monitor protocol health\n> detect exploit\n> emergency withdraw',
    detailedDescription: 'Aegis continuous DeFi monitoring framework. Track protocol health metrics 24/7, detect exploit signatures in real-time, and trigger emergency withdrawal automation to protect positions.',
  },
  {
    id: 'aegis-zk',
    name: 'aegis-zk',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aegis ZK-privacy DeFi layer. Execute DeFi transactions with ZK proofs for private trading, anonymous LPing, and confidential position management.',
    downloads: 6400,
    stars: 320,
    version: '0.9.0',
    installCommand: '> install the aegis-zk skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegis-zk',
    usageExample: '> execute private swap\n> anonymous LP deposit\n> confidential borrow',
    detailedDescription: 'Aegis ZK privacy layer for DeFi. Execute token swaps, liquidity provision, and borrowing with zero-knowledge proofs to hide transaction amounts and counterparty identities.',
  },
  {
    id: 'aegis402-shield',
    name: 'aegis402-shield',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aegis402 shield. MEV-protection wrapper for ERC-4626 vault deposits and withdrawals using order flow privacy and commitment schemes.',
    downloads: 5200,
    stars: 260,
    version: '1.0.0',
    installCommand: '> install the aegis402-shield skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegis402-shield',
    usageExample: '> shield vault deposit\n> private withdrawal\n> commit-reveal swap',
    detailedDescription: 'Aegis402 MEV shield for ERC-4626 vault interactions. Protect deposits and withdrawals from frontrunning using commitment schemes and order flow privacy mechanisms.',
  },
  {
    id: 'aegisprotocol',
    name: 'aegisprotocol',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aegis Protocol core. Unified DeFi security and privacy layer with cross-protocol protection, ZK-based position hiding, and smart alert system.',
    downloads: 13200,
    stars: 660,
    version: '2.0.0',
    installCommand: '> install the aegisprotocol skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegisprotocol',
    usageExample: '> enable aegis protection\n> hide position with ZK\n> set smart alert',
    detailedDescription: 'Aegis Protocol unified core combining DeFi security monitoring, ZK-privacy layer, and smart alert system for comprehensive protection of DeFi positions across all major protocols.',
  },
  {
    id: 'aegiszk',
    name: 'aegiszk',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'AegisZK privacy module. Standalone ZK component for DeFi transaction privacy — integrates with Aave, Uniswap, Curve, and custom protocols.',
    downloads: 4800,
    stars: 240,
    version: '1.0.0',
    installCommand: '> install the aegiszk skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aegiszk',
    usageExample: '> wrap DeFi tx with ZK\n> generate privacy proof\n> verify private txn',
    detailedDescription: 'AegisZK standalone privacy module for DeFi transactions. Wrap Aave, Uniswap, and Curve interactions with ZK proofs for transaction confidentiality and position privacy.',
  },

  // ── AeroDrop X / Aerodrome Variants ──
  {
    id: 'aero-drop-x',
    name: 'aero-drop-x',
    author: 'AeroDrop',
    authorHandle: '@aerodropx',
    tag: 'Skill',
    category: 'defi',
    description: 'Aero Drop X airdrop hunter. Track and claim Aerodrome/Base ecosystem airdrops, farm qualifying activity, and manage airdrop portfolios.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the aero-drop-x skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aero-drop-x',
    usageExample: '> check airdrop eligibility\n> farm qualifying activity\n> claim airdrop',
    detailedDescription: 'Aero Drop X airdrop intelligence for Base ecosystem. Track eligibility requirements, automate qualifying DeFi activity, and claim airdrops from Aerodrome and partner protocols.',
    trending: true,
  },
  {
    id: 'aerodrome-base-agent',
    name: 'aerodrome-base-agent',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome Base agent. Full Aerodrome DEX automation on Base — swaps, LP management, veAERO voting, and gauge bribe optimization.',
    downloads: 24600,
    stars: 1230,
    version: '2.0.0',
    installCommand: '> install the aerodrome-base-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-base-agent',
    usageExample: '> swap on aerodrome\n> manage veAERO\n> vote on gauge',
    detailedDescription: 'Comprehensive Aerodrome DEX automation for Base. Execute swaps with optimal routing, manage liquidity positions, lock AERO for veAERO governance, and optimize bribe yields.',
    featured: true,
  },
  {
    id: 'aerodrome-base-dex',
    name: 'aerodrome-base-dex',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome DEX on Base. Direct access to Aerodrome swap pools, CL positions, and ve(3,3) liquidity mechanics on Base network.',
    downloads: 19800,
    stars: 990,
    version: '1.5.0',
    installCommand: '> install the aerodrome-base-dex skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-base-dex',
    usageExample: '> swap via aerodrome pool\n> open CL position\n> provide ve33 liquidity',
    detailedDescription: 'Direct Aerodrome DEX integration on Base. Interact with stable/volatile swap pools, open concentrated liquidity (CL) positions, and participate in ve(3,3) token mechanics for emissions optimization.',
  },
  {
    id: 'aerodrome-dex-agent',
    name: 'aerodrome-dex-agent',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome DEX automated agent. Intelligent swap routing, position range management, and auto-compounding of fees on Aerodrome CL.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the aerodrome-dex-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-dex-agent',
    usageExample: '> auto-route swap\n> rebalance CL range\n> compound fees',
    detailedDescription: 'Aerodrome DEX automation with intelligent swap routing, automated concentrated liquidity range rebalancing, and fee compounding to maximize LP returns on Base.',
  },
  {
    id: 'aerodrome-dex-base',
    name: 'aerodrome-dex-base',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome DEX Base integration. Protocol-level access to Aerodrome\'s v2 and CL AMM pools on Base with full fee-tier support.',
    downloads: 11600,
    stars: 580,
    version: '1.0.0',
    installCommand: '> install the aerodrome-dex-base skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-dex-base',
    usageExample: '> interact with aerodrome v2\n> select fee tier\n> query pool data',
    detailedDescription: 'Protocol-level Aerodrome DEX access for Base. Interact with both v2 AMM and concentrated liquidity pools, select fee tiers, query real-time pool data, and manage LP tokens.',
  },
  {
    id: 'aerodrome-dex-tools',
    name: 'aerodrome-dex-tools',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome DEX analytics and tools. Pool analytics, fee projection, bribe optimizer, and veAERO voting power calculator.',
    downloads: 8400,
    stars: 420,
    version: '1.0.0',
    installCommand: '> install the aerodrome-dex-tools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-dex-tools',
    usageExample: '> analyze aerodrome pools\n> project fees\n> optimize bribes',
    detailedDescription: 'Aerodrome DEX analytics toolkit covering pool fee projections, bribe ROI optimization, veAERO voting power calculations, and LP impermanent loss analysis.',
  },
  {
    id: 'aerodrome-finance',
    name: 'aerodrome-finance',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome Finance suite. Complete Aerodrome ecosystem access including AERO tokenomics, protocol revenue, and ve(3,3) governance participation.',
    downloads: 22400,
    stars: 1120,
    version: '2.1.0',
    installCommand: '> install the aerodrome-finance skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-finance',
    usageExample: '> access aerodrome suite\n> track AERO tokenomics\n> participate in governance',
    detailedDescription: 'Full Aerodrome Finance ecosystem suite with AERO tokenomics tracking, protocol revenue analytics, ve(3,3) governance participation, and cross-protocol yield optimization on Base.',
  },
  {
    id: 'aerodrome-ve3',
    name: 'aerodrome-ve3',
    author: 'Aerodrome',
    authorHandle: '@aerodromefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Aerodrome ve(3,3) governance agent. Lock AERO, vote on gauge emissions, collect bribes, and optimize veAERO yield across epochs.',
    downloads: 17600,
    stars: 880,
    version: '1.5.0',
    installCommand: '> install the aerodrome-ve3 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrome-ve3',
    usageExample: '> lock AERO for veAERO\n> vote on emissions\n> collect epoch bribes',
    detailedDescription: 'Aerodrome ve(3,3) governance optimization. Lock AERO tokens for veAERO, strategically vote on gauge emissions to maximize bribe returns, collect weekly bribes, and compound veAERO power.',
    trending: true,
  },
  {
    id: 'aerodrop-x',
    name: 'aerodrop-x',
    author: 'AeroDrop',
    authorHandle: '@aerodropx',
    tag: 'Skill',
    category: 'defi',
    description: 'AeroDrop X agent. Simplified airdrop discovery and claiming for Base ecosystem tokens including Aerodrome partner rewards.',
    downloads: 12800,
    stars: 640,
    version: '1.0.0',
    installCommand: '> install the aerodrop-x skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodrop-x',
    usageExample: '> discover airdrops\n> check wallet eligibility\n> claim rewards',
    detailedDescription: 'Simplified airdrop discovery and claiming for Base. Scan wallet addresses for eligible airdrops, check eligibility across multiple protocols, and automate claim transactions.',
  },
  {
    id: 'aerodropx-airdrop-agent',
    name: 'aerodropx-airdrop-agent',
    author: 'AeroDrop',
    authorHandle: '@aerodropx',
    tag: 'Skill',
    category: 'defi',
    description: 'AeroDrop X autonomous airdrop agent. Monitor new airdrop announcements, auto-qualify, and batch claim across multiple wallets.',
    downloads: 9400,
    stars: 470,
    version: '1.0.0',
    installCommand: '> install the aerodropx-airdrop-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodropx-airdrop-agent',
    usageExample: '> monitor new airdrops\n> auto-qualify\n> batch claim',
    detailedDescription: 'Autonomous airdrop farming agent for Base. Monitor announcements, automatically perform qualifying on-chain activity, and execute batch claim transactions across multiple wallet addresses.',
  },
  {
    id: 'aerodropx',
    name: 'aerodropx',
    author: 'AeroDrop',
    authorHandle: '@aerodropx',
    tag: 'Skill',
    category: 'defi',
    description: 'AeroDrop X core. Token airdrop tracking, eligibility checking, and yield strategy integration for Base ecosystem airdrops.',
    downloads: 7600,
    stars: 380,
    version: '1.0.0',
    installCommand: '> install the aerodropx skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerodropx',
    usageExample: '> track airdrop status\n> check eligibility\n> integrate yield',
    detailedDescription: 'AeroDrop X core module for tracking airdrop status, checking wallet eligibility, and integrating claimed tokens into yield strategies for maximum capital efficiency.',
  },
  {
    id: 'aerohunter-ai',
    name: 'aerohunter-ai',
    author: 'AeroHunter',
    authorHandle: '@aerohunter_ai',
    tag: 'Skill',
    category: 'defi',
    description: 'AeroHunter AI agent. AI-powered Aerodrome opportunity hunter — identifies optimal pools, bribes, and veAERO strategies using ML predictions.',
    downloads: 11200,
    stars: 560,
    version: '1.0.0',
    installCommand: '> install the aerohunter-ai skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aerohunter-ai',
    usageExample: '> hunt aerodrome alpha\n> ML bribe prediction\n> optimize veAERO',
    detailedDescription: 'AI-powered Aerodrome strategy optimizer using ML models to predict bribe yields, identify undervalued gauge opportunities, and suggest optimal veAERO allocation for maximum returns.',
  },

  // ── Aevo, Agent Swaps, Agentic XP ──
  {
    id: 'aevo-trading',
    name: 'aevo-trading',
    author: 'Aevo',
    authorHandle: '@aevo_xyz',
    tag: 'Skill',
    category: 'defi',
    description: 'Aevo options and perps trading. Trade crypto options and perpetuals on Aevo — the high-performance on-chain derivatives exchange.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the aevo-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/aevo-trading',
    usageExample: '> buy call option\n> trade aevo perp\n> manage options portfolio',
    detailedDescription: 'Aevo on-chain derivatives integration for trading crypto options (puts/calls) and perpetual futures with sub-millisecond execution on the Aevo L2 order book.',
    trending: true,
  },
  {
    id: 'agent-swaps',
    name: 'agent-swaps',
    author: 'Agent Swaps',
    authorHandle: '@agentswaps',
    tag: 'Skill',
    category: 'defi',
    description: 'Agent-native swap toolkit. Execute token swaps across multiple DEXes with intent-based ordering, slippage protection, and best execution logic.',
    downloads: 21400,
    stars: 1070,
    version: '1.5.0',
    installCommand: '> install the agent-swaps skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/agent-swaps',
    usageExample: '> swap with best execution\n> protect from slippage\n> submit intent order',
    detailedDescription: 'Agent-native token swap infrastructure combining intent-based ordering, multi-DEX routing, MEV protection, and best execution logic for autonomous agents across EVM chains.',
  },
  {
    id: 'agentic-xp',
    name: 'agentic-xp',
    author: 'AgenticXP',
    authorHandle: '@agentic_xp',
    tag: 'Skill',
    category: 'defi',
    description: 'Agentic XP system. Track and accumulate protocol XP/points through automated DeFi interactions — swap, LP, borrow, and earn points.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the agentic-xp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/agentic-xp',
    usageExample: '> earn protocol XP\n> track point multipliers\n> optimize XP per dollar',
    detailedDescription: 'Agentic XP accumulation system automating DeFi interactions to maximize protocol experience points across multiple point programs, tracking multipliers, and optimizing activity schedules.',
  },

  // ── AI Crypto Claude ──
  {
    id: 'ai-crypto-claude',
    name: 'ai-crypto-claude',
    author: 'Claude DeFi',
    authorHandle: '@claude_defi',
    tag: 'Skill',
    category: 'defi',
    description: 'AI-powered DeFi analysis with Claude. Natural language DeFi strategy planning, risk analysis, and trade execution via Claude AI integration.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the ai-crypto-claude skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/ai-crypto-claude',
    usageExample: '> ask claude for DeFi advice\n> analyze risk with AI\n> execute AI strategy',
    detailedDescription: 'Claude AI integration for DeFi decision-making. Ask natural language questions about strategies, receive risk analysis, and execute AI-recommended DeFi actions with full transparency.',
  },

  // ── AlienBase Extended ──
  {
    id: 'alienbase-base-dex-agent',
    name: 'alienbase-base-dex-agent',
    author: 'AlienBase',
    authorHandle: '@alienbase_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'AlienBase DEX automation agent on Base. Automated swaps, LP management, yield farming, and ALB reward compounding.',
    downloads: 10800,
    stars: 540,
    version: '1.0.0',
    installCommand: '> install the alienbase-base-dex-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/alienbase-base-dex-agent',
    usageExample: '> auto-swap on alienbase\n> manage LP positions\n> compound ALB rewards',
    detailedDescription: 'AlienBase DEX automation on Base with intelligent swap routing, LP position management, yield farming automation, and ALB reward compounding to maximize returns.',
  },
  {
    id: 'alienbase-base-dex',
    name: 'alienbase-base-dex',
    author: 'AlienBase',
    authorHandle: '@alienbase_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'AlienBase DEX core on Base. Direct protocol-level access to AlienBase swap pools, liquidity, and token interactions.',
    downloads: 7400,
    stars: 370,
    version: '1.0.0',
    installCommand: '> install the alienbase-base-dex skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/alienbase-base-dex',
    usageExample: '> interact with alienbase pool\n> add liquidity\n> query APR',
    detailedDescription: 'Direct AlienBase DEX protocol access on Base. Interact with swap pools, add/remove liquidity positions, query pool APR data, and manage LP token balances.',
  },
  {
    id: 'alienbase-base-yields',
    name: 'alienbase-base-yields',
    author: 'AlienBase',
    authorHandle: '@alienbase_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'AlienBase yield optimization on Base. Track and maximize yield across AlienBase farms, staking pools, and partnered Base protocols.',
    downloads: 6200,
    stars: 310,
    version: '1.0.0',
    installCommand: '> install the alienbase-base-yields skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/alienbase-base-yields',
    usageExample: '> track alienbase yields\n> optimize farm allocation\n> auto-compound',
    detailedDescription: 'AlienBase yield optimization for Base. Track farming yields across all AlienBase pools, optimize allocation for maximum APR, and automate reward compounding.',
  },
  {
    id: 'alienbase-dex-base',
    name: 'alienbase-dex-base',
    author: 'AlienBase',
    authorHandle: '@alienbase_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'AlienBase DEX Base integration. Multi-pool arbitrage, liquidity analytics, and ve(3,3) participation on AlienBase.',
    downloads: 5800,
    stars: 290,
    version: '1.0.0',
    installCommand: '> install the alienbase-dex-base skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/alienbase-dex-base',
    usageExample: '> arbitrage alienbase pools\n> analyze liquidity\n> vote on emissions',
    detailedDescription: 'Advanced AlienBase DEX access on Base with multi-pool arbitrage scanning, deep liquidity analytics, and ve(3,3) voting for gauge emission direction.',
  },
  {
    id: 'alienbase-farms',
    name: 'alienbase-farms',
    author: 'AlienBase',
    authorHandle: '@alienbase_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'AlienBase farming automation. Deposit LP tokens to AlienBase farms, track APR changes, and auto-harvest ALB rewards.',
    downloads: 8800,
    stars: 440,
    version: '1.0.0',
    installCommand: '> install the alienbase-farms skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/alienbase-farms',
    usageExample: '> deposit to farm\n> track APR changes\n> harvest ALB',
    detailedDescription: 'AlienBase yield farming automation. Deposit LP tokens to farming pools, monitor real-time APR changes, harvest ALB rewards, and auto-compound into LP positions.',
  },
  {
    id: 'alienbase-odos',
    name: 'alienbase-odos',
    author: 'AlienBase',
    authorHandle: '@alienbase_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'AlienBase + Odos integration. Route swaps through Odos aggregator into AlienBase liquidity for optimal pricing and reduced slippage.',
    downloads: 6600,
    stars: 330,
    version: '1.0.0',
    installCommand: '> install the alienbase-odos skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/alienbase-odos',
    usageExample: '> swap via odos to alienbase\n> optimize routing\n> compare slippage',
    detailedDescription: 'AlienBase and Odos DEX aggregator integration. Route swaps through Odos for optimal pricing while providing liquidity to AlienBase pools, minimizing slippage and maximizing fee returns.',
  },

  // ── Arc Pilot ──
  {
    id: 'arc-pilot',
    name: 'arc-pilot',
    author: 'ArcPilot',
    authorHandle: '@arcpilot_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'ArcPilot DeFi navigator. AI-guided DeFi strategy execution with portfolio optimization, risk scoring, and automated rebalancing.',
    downloads: 13600,
    stars: 680,
    version: '1.0.0',
    installCommand: '> install the arc-pilot skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/arc-pilot',
    usageExample: '> navigate defi with AI\n> optimize portfolio\n> auto-rebalance',
    detailedDescription: 'ArcPilot AI-guided DeFi navigator combining portfolio optimization, risk scoring, and automated rebalancing for intelligent position management across DeFi protocols.',
  },
  {
    id: 'arcpilot',
    name: 'arcpilot',
    author: 'ArcPilot',
    authorHandle: '@arcpilot_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'ArcPilot core module. Base DeFi agent with multi-protocol position tracking, yield comparison, and smart allocation.',
    downloads: 9200,
    stars: 460,
    version: '1.0.0',
    installCommand: '> install the arcpilot skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/arcpilot',
    usageExample: '> track all positions\n> compare yields\n> allocate smartly',
    detailedDescription: 'ArcPilot core DeFi module for unified position tracking, cross-protocol yield comparison, and intelligent capital allocation across lending, LP, and staking opportunities.',
  },
  {
    id: 'arcpilot-defi',
    name: 'arcpilot-defi',
    author: 'ArcPilot',
    authorHandle: '@arcpilot_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'ArcPilot DeFi suite. End-to-end DeFi management including yield farming, lending, bridging, and on-chain analytics in one agent.',
    downloads: 11400,
    stars: 570,
    version: '1.5.0',
    installCommand: '> install the arcpilot-defi skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/arcpilot-defi',
    usageExample: '> manage full defi portfolio\n> farm and lend\n> bridge and analyze',
    detailedDescription: 'ArcPilot comprehensive DeFi management suite combining yield farming, lending, cross-chain bridging, and on-chain analytics in a unified agent for complete DeFi portfolio management.',
  },

  // ── Asset Forge, Awaken, Axelrod ──
  {
    id: 'assetaforge',
    name: 'assetaforge',
    author: 'AssetForge',
    authorHandle: '@assetaforge',
    tag: 'Skill',
    category: 'defi',
    description: 'AssetaForge tokenization platform. Tokenize real-world assets, manage on-chain representations, and enable DeFi composability for RWAs.',
    downloads: 8400,
    stars: 420,
    version: '1.0.0',
    installCommand: '> install the assetaforge skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/assetaforge',
    usageExample: '> tokenize RWA\n> manage asset token\n> enable DeFi composability',
    detailedDescription: 'AssetaForge real-world asset tokenization platform. Create on-chain representations of real-world assets, manage token metadata and compliance, and integrate with DeFi protocols for liquidity.',
  },
  {
    id: 'awaken-official-agent-skills',
    name: 'awaken-official-agent-skills',
    author: 'Awaken Finance',
    authorHandle: '@awaken_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Awaken Finance official agent skills. DeFi on aelf blockchain — swap, LP farming, and yield optimization with Awaken DEX.',
    downloads: 7200,
    stars: 360,
    version: '1.0.0',
    installCommand: '> install the awaken-official-agent-skills skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/awaken-official-agent-skills',
    usageExample: '> swap on awaken dex\n> farm LP tokens\n> optimize yield on aelf',
    detailedDescription: 'Official Awaken Finance DeFi agent for aelf blockchain. Execute token swaps, provide liquidity for farming rewards, and optimize yield across Awaken DEX pools.',
  },
  {
    id: 'axelrod-swap-agent',
    name: 'axelrod-swap-agent',
    author: 'Axelrod',
    authorHandle: '@axelrod_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Axelrod cross-chain swap agent. Atomic cross-chain token swaps with MEV protection and optimal route selection across 20+ networks.',
    downloads: 12400,
    stars: 620,
    version: '1.0.0',
    installCommand: '> install the axelrod-swap-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/axelrod-swap-agent',
    usageExample: '> atomic cross-chain swap\n> protect from MEV\n> find optimal route',
    detailedDescription: 'Axelrod atomic cross-chain swap agent with MEV protection. Execute cross-chain swaps with optimal routing across 20+ networks, atomic settlement guarantees, and competitive pricing.',
  },

  // ── Balancer v3 Pools ──
  {
    id: 'balancer-v3-pools',
    name: 'balancer-v3-pools',
    author: 'Balancer',
    authorHandle: '@balancer',
    tag: 'Skill',
    category: 'defi',
    description: 'Balancer v3 pool management. Create and configure custom Balancer weighted/boosted pools, manage pool parameters, and track fee revenue.',
    downloads: 11800,
    stars: 590,
    version: '3.0.0',
    installCommand: '> install the balancer-v3-pools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/balancer-v3-pools',
    usageExample: '> create balancer pool\n> set pool weights\n> track fee revenue',
    detailedDescription: 'Balancer v3 pool creation and management. Deploy custom weighted or boosted pools with configurable parameters, manage swap fees, monitor LP revenue, and integrate with Balancer governance.',
  },

  // ── Bridge Rates MCP ──
  {
    id: 'bridge-rates-mcp',
    name: 'bridge-rates-mcp',
    author: 'Bridge Rates',
    authorHandle: '@bridgerates',
    tag: 'Skill',
    category: 'defi',
    description: 'Bridge rates MCP server. Real-time cross-chain bridge fee comparison via Model Context Protocol for optimal bridging decisions.',
    downloads: 9600,
    stars: 480,
    version: '1.0.0',
    installCommand: '> install the bridge-rates-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/bridge-rates-mcp',
    usageExample: '> compare bridge fees\n> find cheapest route\n> estimate bridge time',
    detailedDescription: 'Bridge rates aggregator via MCP. Compare real-time fees across Hop, Across, Stargate, Synapse, and 20+ bridges. Find optimal routes by cost, speed, and security for cross-chain transfers.',
  },

  // ── Captain Dackie ──
  {
    id: 'captain-dackie',
    name: 'captain-dackie',
    author: 'Captain Dackie',
    authorHandle: '@captdackie',
    tag: 'Skill',
    category: 'defi',
    description: 'Captain Dackie DeFi agent. Quack-powered DeFi on Base — swaps, NFT trading, and meme token farming with community-driven strategies.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the captain-dackie skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/captain-dackie',
    usageExample: '> swap with captain dackie\n> trade dackie NFTs\n> farm meme tokens',
    detailedDescription: 'Captain Dackie community DeFi agent on Base. Execute swaps, trade Dackie NFTs, participate in meme token farming, and access community-driven Base DeFi strategies.',
    trending: true,
  },
  {
    id: 'captain-dackie-defai',
    name: 'captain-dackie-defai',
    author: 'Captain Dackie',
    authorHandle: '@captdackie',
    tag: 'Skill',
    category: 'defi',
    description: 'Captain Dackie DeFAI integration. AI-enhanced DeFi with Dackie personality — automated strategies, sentiment trading, and community signals.',
    downloads: 10400,
    stars: 520,
    version: '1.0.0',
    installCommand: '> install the captain-dackie-defai skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/captain-dackie-defai',
    usageExample: '> run dackie AI strategy\n> trade on sentiment\n> follow community signals',
    detailedDescription: 'Captain Dackie DeFAI module combining AI-driven DeFi execution with community sentiment signals and the distinctive Dackie personality for Base ecosystem trading.',
  },

  // ── Composaible ──
  {
    id: 'composai-able',
    name: 'composai-able',
    author: 'ComposaI',
    authorHandle: '@composai_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'ComposaI DeFi composability AI. Intelligently compose multi-step DeFi transactions using AI planning for maximum capital efficiency.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the composai-able skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/composai-able',
    usageExample: '> compose multi-step DeFi\n> AI-plan transaction\n> maximize efficiency',
    detailedDescription: 'AI-powered DeFi composability engine that plans and executes multi-step transactions — borrow + swap + LP + stake — in a single atomic transaction for maximum capital efficiency.',
  },
  {
    id: 'composaible',
    name: 'composaible',
    author: 'Composaible',
    authorHandle: '@composaible',
    tag: 'Skill',
    category: 'defi',
    description: 'Composaible DeFi agent. Build composable DeFi strategies from modular components and execute complex multi-protocol transactions.',
    downloads: 7200,
    stars: 360,
    version: '1.0.0',
    installCommand: '> install the composaible skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/composaible',
    usageExample: '> build composable strategy\n> combine DeFi modules\n> execute complex tx',
    detailedDescription: 'Composaible modular DeFi strategy builder. Combine lending, swapping, yield farming, and bridging modules into complex multi-protocol strategies and execute atomically.',
  },

  // ── Convex Extended ──
  {
    id: 'convex-agent',
    name: 'convex-agent',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex Finance automation agent. Auto-deposit Curve LP, compound CVX/CRV rewards, and manage vlCVX voting for optimal emissions capture.',
    downloads: 16800,
    stars: 840,
    version: '2.0.0',
    installCommand: '> install the convex-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-agent',
    usageExample: '> auto-deposit to convex\n> compound rewards\n> vote vlCVX',
    detailedDescription: 'Convex Finance automation agent with auto-deposit of Curve LP tokens, automated reward compounding, and vlCVX voting optimization for maximum CRV/CVX emissions capture.',
    trending: true,
  },
  {
    id: 'convex-crv-agent',
    name: 'convex-crv-agent',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex CRV management agent. Optimize cvxCRV positions, track CRV boost, and maximize staking yields on Convex.',
    downloads: 11200,
    stars: 560,
    version: '1.5.0',
    installCommand: '> install the convex-crv-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-crv-agent',
    usageExample: '> manage cvxCRV\n> track CRV boost\n> maximize staking yield',
    detailedDescription: 'Convex CRV management module for optimizing cvxCRV positions, tracking boost multipliers, harvesting CRV/CVX rewards, and maximizing staking yields through optimal pool selection.',
  },
  {
    id: 'convex-crv-boost',
    name: 'convex-crv-boost',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex CRV boost optimizer. Maximize Curve LP boost via Convex — track boost factors, compare pools, and allocate optimally.',
    downloads: 9400,
    stars: 470,
    version: '1.0.0',
    installCommand: '> install the convex-crv-boost skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-crv-boost',
    usageExample: '> check boost factor\n> compare pool boosts\n> optimize allocation',
    detailedDescription: 'Convex CRV boost optimization tool. Track and compare boost factors across Curve pools, calculate optimal veCRV allocation, and maximize CRV emission yields via Convex boosting.',
  },
  {
    id: 'convex-crv-staking',
    name: 'convex-crv-staking',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex CRV staking suite. Stake CRV via Convex, manage cvxCRV positions, lock for vlCVX, and track multi-reward streams.',
    downloads: 12600,
    stars: 630,
    version: '1.5.0',
    installCommand: '> install the convex-crv-staking skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-crv-staking',
    usageExample: '> stake CRV on convex\n> manage cvxCRV\n> track reward streams',
    detailedDescription: 'Convex CRV staking comprehensive suite. Convert CRV to cvxCRV for boosted rewards, lock CVX for vlCVX governance, and track combined CRV + CVX + 3CRV reward streams.',
  },
  {
    id: 'convex-curve-booster',
    name: 'convex-curve-booster',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex-Curve yield booster. Boost Curve pool yields via Convex veCRV accumulation — deposit, boost, and harvest optimized yields.',
    downloads: 14800,
    stars: 740,
    version: '2.0.0',
    installCommand: '> install the convex-curve-booster skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-curve-booster',
    usageExample: '> boost curve pool\n> deposit with boost\n> harvest boosted yield',
    detailedDescription: 'Convex-Curve yield optimization booster. Leverage Convex\'s aggregated veCRV for maximum Curve pool boosts, deposit LP tokens with automatic boost application, and harvest enhanced yields.',
  },
  {
    id: 'convex-curve-staking',
    name: 'convex-curve-staking',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex Curve staking integration. Unified interface for staking Curve LP tokens on Convex with automatic boost and reward tracking.',
    downloads: 10600,
    stars: 530,
    version: '1.5.0',
    installCommand: '> install the convex-curve-staking skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-curve-staking',
    usageExample: '> stake curve LP on convex\n> track auto-boost\n> claim combined rewards',
    detailedDescription: 'Unified Convex Curve staking interface. Stake Curve LP tokens with automatic boost application, track boosted APY, claim combined CRV and CVX rewards, and monitor position health.',
  },
  {
    id: 'convex-yield-agent',
    name: 'convex-yield-agent',
    author: 'Convex Finance',
    authorHandle: '@convexfinance',
    tag: 'Skill',
    category: 'defi',
    description: 'Convex yield optimization agent. Intelligently allocate between Convex pools for maximum risk-adjusted yield with auto-compounding.',
    downloads: 13400,
    stars: 670,
    version: '1.5.0',
    installCommand: '> install the convex-yield-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/convex-yield-agent',
    usageExample: '> find best convex pool\n> optimize risk-adjusted yield\n> auto-compound',
    detailedDescription: 'Convex yield optimization agent that analyzes pools by risk-adjusted APY, automatically allocates capital for maximum returns, and compounds rewards back into the optimal strategy.',
  },

  // ── CoW Protocol Extended ──
  {
    id: 'cow-protocol-tools',
    name: 'cow-protocol-tools',
    author: 'CoW Protocol',
    authorHandle: '@cowprotocol',
    tag: 'Skill',
    category: 'defi',
    description: 'CoW Protocol toolkit. Developer tools for building on CoW Protocol — intent composing, solver submission, and surplus analytics.',
    downloads: 8200,
    stars: 410,
    version: '1.0.0',
    installCommand: '> install the cow-protocol-tools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/cow-protocol-tools',
    usageExample: '> compose trade intent\n> submit to solver\n> analyze surplus',
    detailedDescription: 'CoW Protocol developer toolkit for intent creation, solver submission, and surplus analytics. Build custom intents, query solver competition data, and optimize for maximum surplus capture.',
  },
  {
    id: 'cow-swap-cow-protocol',
    name: 'cow-swap-cow-protocol',
    author: 'CoW Protocol',
    authorHandle: '@cowprotocol',
    tag: 'Skill',
    category: 'defi',
    description: 'CoW Swap via CoW Protocol. Execute MEV-protected batch swaps with surplus sharing across Ethereum, Gnosis Chain, and Base.',
    downloads: 16400,
    stars: 820,
    version: '1.5.0',
    installCommand: '> install the cow-swap-cow-protocol skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/cow-swap-cow-protocol',
    usageExample: '> swap via cow protocol\n> get best batch price\n> receive surplus',
    detailedDescription: 'CoW Swap direct integration via CoW Protocol batch auctions. Submit trade intents, participate in batch settlements for optimal prices, and automatically receive trade surplus.',
  },
  {
    id: 'cow-swap-intents',
    name: 'cow-swap-intents',
    author: 'CoW Protocol',
    authorHandle: '@cowprotocol',
    tag: 'Skill',
    category: 'defi',
    description: 'CoW Swap intent-based trading. Submit advanced trade intents with custom conditions, partial fill preferences, and expiry management.',
    downloads: 11200,
    stars: 560,
    version: '1.0.0',
    installCommand: '> install the cow-swap-intents skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/cow-swap-intents',
    usageExample: '> submit conditional intent\n> set partial fill\n> manage intent expiry',
    detailedDescription: 'CoW Swap intent management with conditional execution, partial fill support, intent expiry controls, and advanced order types for DeFi power users and agent automation.',
  },
  {
    id: 'cow-swap-mev-protection',
    name: 'cow-swap-mev-protection',
    author: 'CoW Protocol',
    authorHandle: '@cowprotocol',
    tag: 'Skill',
    category: 'defi',
    description: 'CoW Swap MEV protection layer. Detailed MEV exposure analysis, protection configuration, and comparison vs standard DEX execution.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the cow-swap-mev-protection skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/cow-swap-mev-protection',
    usageExample: '> analyze MEV exposure\n> configure protection\n> compare vs uniswap',
    detailedDescription: 'CoW Swap MEV protection analysis. Quantify MEV exposure on planned trades, configure protection parameters, and compare execution quality against standard DEX venues.',
  },
  {
    id: 'cowswap-intent-based-swaps',
    name: 'cowswap-intent-based-swaps',
    author: 'CoW Protocol',
    authorHandle: '@cowprotocol',
    tag: 'Skill',
    category: 'defi',
    description: 'CoW Swap intent-based swap execution. ERC-1271 and off-chain signature-based intents for gasless trading on CoW Protocol.',
    downloads: 7400,
    stars: 370,
    version: '1.0.0',
    installCommand: '> install the cowswap-intent-based-swaps skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/cowswap-intent-based-swaps',
    usageExample: '> gasless cow swap\n> sign ERC-1271 intent\n> off-chain signature swap',
    detailedDescription: 'CoW Protocol gasless swap execution using ERC-1271 smart contract signatures and off-chain signature intents. Enable trading without gas costs through CoW Protocol solver settlement.',
  },

  // ── Crawbot / Superfluid ──
  {
    id: 'crawbot-superfluid',
    name: 'crawbot-superfluid',
    author: 'Crawbot',
    authorHandle: '@crawbot_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Crawbot Superfluid integration. Automate real-time token streaming with Superfluid — salary streams, DCA streams, and conditional flows.',
    downloads: 8400,
    stars: 420,
    version: '1.0.0',
    installCommand: '> install the crawbot-superfluid skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/crawbot-superfluid',
    usageExample: '> create token stream\n> automate salary payment\n> conditional DCA stream',
    detailedDescription: 'Crawbot Superfluid automation for real-time token streaming. Create salary streams, DCA payment flows, and conditional streaming agreements using Superfluid Framework.',
    trending: true,
  },
  {
    id: 'crawbot-superfluid-specialist',
    name: 'crawbot-superfluid-specialist',
    author: 'Crawbot',
    authorHandle: '@crawbot_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Crawbot Superfluid specialist. Advanced Superfluid features — Super Token creation, GDA pools, and distribution agreements at scale.',
    downloads: 6200,
    stars: 310,
    version: '1.0.0',
    installCommand: '> install the crawbot-superfluid-specialist skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/crawbot-superfluid-specialist',
    usageExample: '> create super token\n> setup GDA pool\n> manage distribution',
    detailedDescription: 'Advanced Superfluid specialist skills covering Super Token creation, General Distribution Agreement (GDA) pool management, and large-scale token distribution for DAOs and protocols.',
  },
  {
    id: 'crawbot8004',
    name: 'crawbot8004',
    author: 'Crawbot',
    authorHandle: '@crawbot_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Crawbot ERC-8004 DeFi agent. ERC-8004 agent identity integrated with DeFi — streaming payments, reputation-gated access, and trustless DeFi.',
    downloads: 5600,
    stars: 280,
    version: '1.0.0',
    installCommand: '> install the crawbot8004 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/crawbot8004',
    usageExample: '> identity-gated DeFi\n> stream with ERC-8004\n> reputation access',
    detailedDescription: 'Crawbot ERC-8004 DeFi integration combining agent identity with Superfluid streaming, reputation-gated DeFi protocol access, and trustless agent-to-agent payment channels.',
  },

  // ── Curve Extended ──
  {
    id: 'curve-dex-agent',
    name: 'curve-dex-agent',
    author: 'Curve Finance',
    authorHandle: '@curvefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Curve DEX automation agent. Intelligent routing across Curve pools, auto-rebalancing, and CRV reward optimization with auto-compound.',
    downloads: 22400,
    stars: 1120,
    version: '2.0.0',
    installCommand: '> install the curve-dex-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/curve-dex-agent',
    usageExample: '> route via curve\n> auto-rebalance LP\n> compound CRV rewards',
    detailedDescription: 'Curve Finance DEX automation agent with intelligent cross-pool routing, automatic LP rebalancing, and CRV reward auto-compounding for maximized stablecoin yield.',
  },
  {
    id: 'curve-finance-pools',
    name: 'curve-finance-pools',
    author: 'Curve Finance',
    authorHandle: '@curvefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Curve Finance pool management. Deploy new Curve pools, configure pool parameters, and manage liquidity for stablecoins and pegged assets.',
    downloads: 14600,
    stars: 730,
    version: '2.0.0',
    installCommand: '> install the curve-finance-pools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/curve-finance-pools',
    usageExample: '> deploy curve pool\n> set A parameter\n> manage pool liquidity',
    detailedDescription: 'Curve Finance pool lifecycle management. Deploy custom stableswap pools with configurable amplification parameters, manage liquidity depth, and monitor pool health metrics.',
  },
  {
    id: 'curve-pools',
    name: 'curve-pools',
    author: 'Curve Finance',
    authorHandle: '@curvefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Curve pools aggregator. Browse and interact with all Curve pools — stableswap, tricrypto, factory, and lending markets.',
    downloads: 19200,
    stars: 960,
    version: '2.0.0',
    installCommand: '> install the curve-pools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/curve-pools',
    usageExample: '> browse all curve pools\n> compare APY\n> add liquidity',
    detailedDescription: 'Comprehensive Curve pool browser and interaction layer. Discover all pool types (stableswap, tricrypto, factory pools, crvUSD lending markets), compare APYs, and manage liquidity positions.',
  },
  {
    id: 'curve-stable-swap',
    name: 'curve-stable-swap',
    author: 'Curve Finance',
    authorHandle: '@curvefi',
    tag: 'Skill',
    category: 'defi',
    description: 'Curve StableSwap specialist. Optimized stablecoin swapping via Curve StableSwap with minimal slippage and deep liquidity access.',
    downloads: 24800,
    stars: 1240,
    version: '2.0.0',
    installCommand: '> install the curve-stable-swap skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/curve-stable-swap',
    usageExample: '> swap stablecoins via curve\n> minimize slippage\n> access deep liquidity',
    detailedDescription: 'Curve StableSwap specialist for low-slippage stablecoin exchanges. Access deep liquidity across USDC/USDT/DAI/FRAX pools, optimize swap routes, and execute large trades with minimal price impact.',
  },

  // ── DarwinFi Extended ──
  {
    id: 'darwinfi-evolution',
    name: 'darwinfi-evolution',
    author: 'DarwinFi',
    authorHandle: '@darwinfi_io',
    tag: 'Skill',
    category: 'defi',
    description: 'DarwinFi evolution engine. Run genetic algorithm simulations for DeFi strategy evolution with configurable fitness functions and mutation rates.',
    downloads: 5400,
    stars: 270,
    version: '0.9.0',
    installCommand: '> install the darwinfi-evolution skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/darwinfi-evolution',
    usageExample: '> run evolution sim\n> configure fitness\n> set mutation rate',
    detailedDescription: 'DarwinFi genetic algorithm engine for DeFi. Configure fitness functions (yield, Sharpe ratio, drawdown), run multi-generation strategy evolution, and deploy the fittest strategies.',
  },
  {
    id: 'darwinfi-trading',
    name: 'darwinfi-trading',
    author: 'DarwinFi',
    authorHandle: '@darwinfi_io',
    tag: 'Skill',
    category: 'defi',
    description: 'DarwinFi trading agent. Execute evolved trading strategies on DEXes with continuous fitness-based optimization and real market feedback loops.',
    downloads: 7200,
    stars: 360,
    version: '0.9.0',
    installCommand: '> install the darwinfi-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/darwinfi-trading',
    usageExample: '> execute evolved strategy\n> run fitness feedback\n> adapt to market',
    detailedDescription: 'DarwinFi trading execution agent. Deploy evolved strategies on DEXes, collect real-time performance feedback for continuous strategy optimization, and adapt gene pools to market conditions.',
  },

  // ── DeepBlue, DeFi Primitives, DefiLlama ──
  {
    id: 'deepblue-defi-api',
    name: 'deepblue-defi-api',
    author: 'DeepBlue',
    authorHandle: '@deepblue_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'DeepBlue DeFi API. Aggregated DeFi data APIs for TVL, yields, liquidations, and protocol health across 1000+ protocols.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the deepblue-defi-api skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/deepblue-defi-api',
    usageExample: '> query protocol TVL\n> get yield data\n> check liquidation data',
    detailedDescription: 'DeepBlue aggregated DeFi data platform. Query TVL, yield rates, liquidation volumes, and protocol health metrics across 1000+ DeFi protocols for comprehensive market intelligence.',
  },
  {
    id: 'defi-lending',
    name: 'defi-lending',
    author: 'DeFi Hub',
    authorHandle: '@defi_hub',
    tag: 'Skill',
    category: 'defi',
    description: 'Universal DeFi lending aggregator. Compare and interact with Aave, Compound, Euler, Morpho, and 20+ lending protocols in a single interface.',
    downloads: 32400,
    stars: 1620,
    version: '2.0.0',
    installCommand: '> install the defi-lending skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/defi-lending',
    usageExample: '> compare lending rates\n> supply to best protocol\n> borrow at lowest rate',
    detailedDescription: 'Universal DeFi lending aggregator covering Aave v2/v3, Compound v2/v3, Euler, Morpho, Silo, Radiant, and 15+ other lending protocols with unified supply/borrow interface.',
    featured: true,
  },
  {
    id: 'defi-staking',
    name: 'defi-staking',
    author: 'DeFi Hub',
    authorHandle: '@defi_hub',
    tag: 'Skill',
    category: 'defi',
    description: 'Universal DeFi staking hub. Stake tokens across native protocols, liquid staking, and validator staking for maximum staking yield.',
    downloads: 28600,
    stars: 1430,
    version: '2.0.0',
    installCommand: '> install the defi-staking skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/defi-staking',
    usageExample: '> compare staking yields\n> stake for LST\n> manage validator staking',
    detailedDescription: 'Universal staking hub aggregating native token staking, liquid staking derivatives, and validator delegation. Compare yields across ETH, SOL, ATOM, DOT, and 30+ staking opportunities.',
    trending: true,
  },
  {
    id: 'defi-swap',
    name: 'defi-swap',
    author: 'DeFi Hub',
    authorHandle: '@defi_hub',
    tag: 'Skill',
    category: 'defi',
    description: 'Universal DeFi swap interface. Execute token swaps with best-price routing across all major DEXes and aggregators in one command.',
    downloads: 42600,
    stars: 2130,
    version: '2.0.0',
    installCommand: '> install the defi-swap skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/defi-swap',
    usageExample: '> swap any token\n> best price auto-route\n> one-command execution',
    detailedDescription: 'Universal DeFi swap with automatic best-price routing across Uniswap, 1inch, 0x, Paraswap, CoW, and 50+ venues. One-command execution with slippage protection and gas optimization.',
    featured: true,
    trending: true,
  },
  {
    id: 'defi-yield',
    name: 'defi-yield',
    author: 'DeFi Hub',
    authorHandle: '@defi_hub',
    tag: 'Skill',
    category: 'defi',
    description: 'Universal DeFi yield optimizer. Discover and capture the best on-chain yields across lending, LP farming, and staking protocols.',
    downloads: 38200,
    stars: 1910,
    version: '2.0.0',
    installCommand: '> install the defi-yield skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/defi-yield',
    usageExample: '> find best yield\n> auto-deposit to top protocol\n> track total returns',
    detailedDescription: 'Universal DeFi yield optimizer scanning lending, LP farming, staking, and vault opportunities for maximum risk-adjusted APY. Auto-deposits to the highest-yielding compliant protocol.',
    featured: true,
  },
  {
    id: 'defi-yields-mcp',
    name: 'defi-yields-mcp',
    author: 'DeFi Hub',
    authorHandle: '@defi_hub',
    tag: 'Skill',
    category: 'defi',
    description: 'DeFi yields via MCP. Access real-time DeFi yield data across all major protocols through Model Context Protocol for AI agent integration.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the defi-yields-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/defi-yields-mcp',
    usageExample: '> query yields via MCP\n> compare protocols\n> track yield changes',
    detailedDescription: 'DeFi yields aggregator via Model Context Protocol. Query real-time APY data, historical yield trends, and protocol comparisons for AI-driven yield optimization decisions.',
  },
  {
    id: 'defillama-yield-compare',
    name: 'defillama-yield-compare',
    author: 'DefiLlama',
    authorHandle: '@defillama',
    tag: 'Skill',
    category: 'defi',
    description: 'DeFiLlama yield comparison tool. Compare yields across 2000+ DeFi pools using DefiLlama data with risk scoring and historical APY charts.',
    downloads: 34200,
    stars: 1710,
    version: '1.0.0',
    installCommand: '> install the defillama-yield-compare skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/defillama-yield-compare',
    usageExample: '> compare all yields\n> filter by risk\n> view APY history',
    detailedDescription: 'DeFiLlama yield comparison using live API data. Browse 2000+ pools with risk scoring, IL exposure, reward token analysis, and 90-day APY history for informed yield strategy decisions.',
    featured: true,
    trending: true,
  },

  // ── DexPaprika ──
  {
    id: 'dexpaprika-dex-aggregator',
    name: 'dexpaprika-dex-aggregator',
    author: 'DexPaprika',
    authorHandle: '@dexpaprika',
    tag: 'Skill',
    category: 'defi',
    description: 'DexPaprika DEX aggregator. Access cross-chain DEX liquidity data and execute aggregated swaps via DexPaprika\'s unified interface.',
    downloads: 11600,
    stars: 580,
    version: '1.0.0',
    installCommand: '> install the dexpaprika-dex-aggregator skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/dexpaprika-dex-aggregator',
    usageExample: '> aggregate DEX liquidity\n> compare prices\n> execute best swap',
    detailedDescription: 'DexPaprika cross-chain DEX aggregator providing unified liquidity access, price comparison across DEXes, and optimized swap execution via DexPaprika\'s routing infrastructure.',
  },
  {
    id: 'dexpaprika-dex-scanner',
    name: 'dexpaprika-dex-scanner',
    author: 'DexPaprika',
    authorHandle: '@dexpaprika',
    tag: 'Skill',
    category: 'defi',
    description: 'DexPaprika DEX scanner. Scan new token listings, liquidity changes, volume anomalies, and emerging opportunities across all tracked DEXes.',
    downloads: 16800,
    stars: 840,
    version: '1.0.0',
    installCommand: '> install the dexpaprika-dex-scanner skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/dexpaprika-dex-scanner',
    usageExample: '> scan new listings\n> detect volume anomaly\n> find emerging pools',
    detailedDescription: 'DexPaprika real-time DEX scanner monitoring new token listings, liquidity migrations, trading volume anomalies, and trending pools across 50+ DEXes for early opportunity identification.',
    trending: true,
  },

  // ── EigenLayer Extended ──
  {
    id: 'eigenlayer-agent',
    name: 'eigenlayer-agent',
    author: 'EigenLayer',
    authorHandle: '@eigenlayer',
    tag: 'Skill',
    category: 'defi',
    description: 'EigenLayer core agent. Manage restaking positions, monitor AVS health, track slashing risks, and optimize operator delegation on EigenLayer.',
    downloads: 24800,
    stars: 1240,
    version: '1.5.0',
    installCommand: '> install the eigenlayer-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/eigenlayer-agent',
    usageExample: '> manage restaking\n> check AVS health\n> optimize operator',
    detailedDescription: 'EigenLayer comprehensive agent for managing restaking positions, monitoring AVS operational health, calculating slashing probabilities, and optimizing operator delegation for maximum rewards.',
  },
  {
    id: 'eigenlayer-restaking',
    name: 'eigenlayer-restaking',
    author: 'EigenLayer',
    authorHandle: '@eigenlayer',
    tag: 'Skill',
    category: 'defi',
    description: 'EigenLayer restaking core. Restake ETH and LSTs to Actively Validated Services with reward tracking and risk management.',
    downloads: 18600,
    stars: 930,
    version: '1.5.0',
    installCommand: '> install the eigenlayer-restaking skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/eigenlayer-restaking',
    usageExample: '> restake stETH\n> delegate to AVS\n> track restaking rewards',
    detailedDescription: 'EigenLayer restaking core for delegating staked ETH and LSTs (stETH, rETH, cbETH) to AVSes. Track earned rewards, monitor operator performance, and manage restaking positions.',
  },
  {
    id: 'eigenlayer-restaking-tools',
    name: 'eigenlayer-restaking-tools',
    author: 'EigenLayer',
    authorHandle: '@eigenlayer',
    tag: 'Skill',
    category: 'defi',
    description: 'EigenLayer restaking tools. Portfolio analysis, AVS comparison, withdrawal queue management, and restaking strategy optimization.',
    downloads: 12400,
    stars: 620,
    version: '1.0.0',
    installCommand: '> install the eigenlayer-restaking-tools skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/eigenlayer-restaking-tools',
    usageExample: '> analyze restaking portfolio\n> compare AVS returns\n> manage withdrawals',
    detailedDescription: 'EigenLayer restaking analytics toolkit. Compare AVS risk/reward profiles, analyze portfolio diversification, manage withdrawal queues, and optimize restaking strategy allocation.',
  },

  // ── Elsa, Esusu ──
  {
    id: 'elsa-official-openclaw',
    name: 'elsa-official-openclaw',
    author: 'Elsa Finance',
    authorHandle: '@elsa_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Elsa Finance OpenClaw agent. NFT-collateralized lending, fractional NFT trading, and NFT yield farming via Elsa Protocol.',
    downloads: 9200,
    stars: 460,
    version: '1.0.0',
    installCommand: '> install the elsa-official-openclaw skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/elsa-official-openclaw',
    usageExample: '> borrow against NFT\n> trade fractional NFT\n> farm NFT yield',
    detailedDescription: 'Elsa Finance NFT DeFi agent using the OpenClaw protocol. Borrow against NFT collateral, trade fractional NFT shares, and participate in NFT yield farming strategies.',
  },
  {
    id: 'esusu-ai',
    name: 'esusu-ai',
    author: 'Esusu Finance',
    authorHandle: '@esusu_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Esusu AI savings agent. Community-based savings circles on-chain with AI optimization, rotating credit pools, and yield enhancement.',
    downloads: 7800,
    stars: 390,
    version: '1.0.0',
    installCommand: '> install the esusu-ai skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/esusu-ai',
    usageExample: '> join savings circle\n> contribute to pool\n> collect rotating payout',
    detailedDescription: 'Esusu AI community savings on blockchain. Join rotating savings credit associations (ROSCAs) with AI-optimized contribution schedules, yield enhancement while waiting, and automated payout distribution.',
  },
  {
    id: 'esusu-savings-agent',
    name: 'esusu-savings-agent',
    author: 'Esusu Finance',
    authorHandle: '@esusu_fi',
    tag: 'Skill',
    category: 'defi',
    description: 'Esusu savings automation agent. Automate contributions to Esusu savings circles, track payout schedules, and optimize savings yield.',
    downloads: 5800,
    stars: 290,
    version: '1.0.0',
    installCommand: '> install the esusu-savings-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/esusu-savings-agent',
    usageExample: '> automate savings\n> track payout schedule\n> optimize yield',
    detailedDescription: 'Esusu savings automation for blockchain-based ROSCAs. Automate regular contributions, track upcoming payout dates, and optimize yield on idle savings waiting for distribution.',
  },

  // ── ETH Staking Extended ──
  {
    id: 'eth-liquid-staking-compare',
    name: 'eth-liquid-staking-compare',
    author: 'LST Compare',
    authorHandle: '@lstcompare',
    tag: 'Skill',
    category: 'defi',
    description: 'ETH liquid staking comparison tool. Side-by-side comparison of all major LST protocols by APY, safety, liquidity, and decentralization.',
    downloads: 22400,
    stars: 1120,
    version: '1.0.0',
    installCommand: '> install the eth-liquid-staking-compare skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/eth-liquid-staking-compare',
    usageExample: '> compare LST protocols\n> rank by safety score\n> view decentralization',
    detailedDescription: 'Comprehensive ETH liquid staking protocol comparison. Rank stETH, rETH, frxETH, cbETH, ankrETH, and 20+ LSTs by APY, slashing coverage, liquidity depth, and validator decentralization metrics.',
    trending: true,
  },
  {
    id: 'eth-staking-router',
    name: 'eth-staking-router',
    author: 'Staking Router',
    authorHandle: '@staking_router',
    tag: 'Skill',
    category: 'defi',
    description: 'ETH staking router. Intelligent ETH staking allocation across protocols based on risk preference, APY, and exit liquidity requirements.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the eth-staking-router skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/eth-staking-router',
    usageExample: '> route ETH stake\n> select by risk\n> manage exit liquidity',
    detailedDescription: 'ETH staking intelligent router allocating stake across Lido, Rocket Pool, Frax, Stakewise, and native staking based on user risk tolerance, yield targets, and exit liquidity requirements.',
  },

  // ── Ethena Stablecoin ──
  {
    id: 'ethena-stablecoin-agent',
    name: 'ethena-stablecoin-agent',
    author: 'Ethena Labs',
    authorHandle: '@ethena_labs',
    tag: 'Skill',
    category: 'defi',
    description: 'Ethena stablecoin strategy agent. Advanced USDe/sUSDe yield strategies, funding rate monitoring, and automated position management.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the ethena-stablecoin-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/defi/ethena-stablecoin-agent',
    usageExample: '> maximize sUSDe yield\n> monitor funding rate\n> manage USDe position',
    detailedDescription: 'Ethena stablecoin advanced strategy agent. Optimize USDe/sUSDe allocation, monitor ETH perpetual funding rates that drive protocol yield, and automate position management for maximum returns.',
    trending: true,
  },
];